/**
 * Statické zásady ochrany osobních údajů (GDPR) pro veřejné stránky.
 * Obsah odpovídá reálnému zpracování v aplikaci: přihlášky, docházka,
 * fakturace, marketingové e-maily, diagnostický dotazník a ochrana
 * formulářů přes Cloudflare Turnstile.
 */
export function GdprPage() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-16" data-testid="hca-gdpr-page">
      <div className="max-w-3xl">
        <h1 className="mb-4 text-4xl font-bold tracking-tight">Zásady ochrany osobních údajů</h1>
        <p className="mb-2 text-muted-foreground">
          Tyto zásady popisují, jaké osobní údaje zpracováváme, proč, na jakém právním základě, jak
          dlouho je uchováváme a jaká máte práva. Vaše soukromí bereme vážně a údaje zpracováváme v
          souladu s nařízením{" "}
          <abbr title="Nařízení Evropského parlamentu a Rady (EU) 2016/679">GDPR</abbr> a zákonem č.
          110/2019 Sb., o zpracování osobních údajů.
        </p>
        <p className="text-sm text-muted-foreground">Účinné od 4. června 2026.</p>
      </div>

      <div className="prose prose-slate mt-8 max-w-3xl space-y-6 dark:prose-invert">
        <section>
          <h2 className="text-2xl font-semibold">Správce osobních údajů</h2>
          <p className="text-muted-foreground">
            Správcem je společnost <strong className="text-foreground">HCA Czechia s.r.o.</strong>,
            IČO 23530936, DIČ CZ23530936, se sídlem V aleji 238/57, Holásky, 620 00 Brno, zapsaná v
            obchodním rejstříku, provozující vzdělávací programy pod značkou HCA Moravia.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>
              <strong className="text-foreground">E-mail:</strong>{" "}
              <a className="underline" href="mailto:organizace@hcamoravia.cz">
                organizace@hcamoravia.cz
              </a>
            </li>
            <li>
              <strong className="text-foreground">Korespondenční adresa:</strong> Úlehlova 20a,
              Brno-Líšeň, 628 00
            </li>
          </ul>
          <p className="text-muted-foreground">
            Nejmenovali jsme pověřence pro ochranu osobních údajů, protože nám tato povinnost ze
            zákona nevzniká. Ve všech záležitostech ochrany údajů se obracejte na výše uvedený
            e-mail.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">1. Jaké údaje zpracováváme a odkud</h2>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>
              <strong className="text-foreground">Identifikační a kontaktní:</strong> jméno,
              příjmení, e-mail, telefon, název firmy a pracovní pozice
            </li>
            <li>
              <strong className="text-foreground">Fakturační:</strong> IČO, DIČ, adresa firmy a
              bankovní spojení (pouze pro vystavení a úhradu faktur)
            </li>
            <li>
              <strong className="text-foreground">Údaje o účasti:</strong> přihlášky na semináře,
              záznamy docházky, hodnocení pozorovatelem a zpětná vazba
            </li>
            <li>
              <strong className="text-foreground">Údaje z diagnostiky:</strong> odpovědi vyplněné v
              online diagnostickém dotazníku
            </li>
            <li>
              <strong className="text-foreground">Marketingové:</strong> stav odběru novinek,
              udělený souhlas a historie komunikace
            </li>
            <li>
              <strong className="text-foreground">Technické:</strong> IP adresa, údaje nezbytné pro
              ochranu formulářů před boty a spamem a provozní logy
            </li>
          </ul>
          <p className="text-muted-foreground">
            Údaje získáváme přímo od vás (z formulářů, přihlášek nebo dotykového kiosku na
            seminářích) a z průběhu semináře (hodnocení lektorem či pozorovatelem). U firemních
            účastníků nám část údajů může předat zaměstnavatel, který vás na program přihlásí.
          </p>
          <p className="text-muted-foreground">
            Poskytnutí identifikačních a fakturačních údajů je nezbytné pro uzavření a plnění
            smlouvy (bez nich se nelze zúčastnit semináře ani vystavit fakturu). Údaje pro
            marketing, diagnostický dotazník a zpětnou vazbu poskytujete dobrovolně.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">2. Účely a právní základ zpracování</h2>
          <p className="text-muted-foreground">
            Každý údaj zpracováváme jen pro konkrétní účel a na odpovídajícím právním základě podle
            čl. 6 GDPR:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>
              <strong className="text-foreground">Plnění smlouvy</strong> (čl. 6 odst. 1 písm. b):
              organizace a účast na semináři, evidence docházky, komunikace o objednávce a vystavení
              faktury
            </li>
            <li>
              <strong className="text-foreground">Právní povinnost</strong> (čl. 6 odst. 1 písm. c):
              uchování účetních a daňových dokladů
            </li>
            <li>
              <strong className="text-foreground">Souhlas</strong> (čl. 6 odst. 1 písm. a): zvlášť
              pro zasílání marketingových novinek, zvlášť pro online diagnostický dotazník a zvlášť
              pro nepovinnou zpětnou vazbu. Každý souhlas je dobrovolný a můžete jej kdykoli
              samostatně odvolat, aniž to ovlivní ostatní zpracování.
            </li>
            <li>
              <strong className="text-foreground">Oprávněný zájem</strong> (čl. 6 odst. 1 písm. f):
              zabezpečení webu a ochrana formulářů před spamem (Cloudflare Turnstile), hodnocení
              kvality semináře lektorem či pozorovatelem, nabídka vlastních obdobných služeb
              stávajícím klientům a ochrana našich právních nároků. Proti tomuto zpracování můžete
              kdykoli vznést námitku.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">3. Doba uchovávání</h2>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>
              Daňové doklady (faktury): <strong>10 let</strong> (§ 35 zákona č. 235/2004 Sb., o dani
              z přidané hodnoty); ostatní účetní záznamy <strong>5 let</strong> (zákon č. 563/1991
              Sb., o účetnictví)
            </li>
            <li>
              Smluvní a docházková agenda: po dobu spolupráce a následně po dobu promlčecí lhůty pro
              uplatnění nároků (zpravidla 3–4 roky)
            </li>
            <li>Marketingový souhlas: do odvolání souhlasu</li>
            <li>
              Souhlas udělený na kiosku: <strong>3 roky</strong>
            </li>
            <li>
              Diagnostický dotazník: <strong>2 roky</strong>
            </li>
            <li>Technické a provozní logy: zpravidla do 12 měsíců</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">4. Komu údaje předáváme</h2>
          <p className="text-muted-foreground">
            Údaje nikdy neprodáváme. Zpřístupňujeme je jen v nezbytném rozsahu těmto kategoriím
            příjemců:
          </p>
          <p className="text-muted-foreground">
            <strong className="text-foreground">Zpracovatelé</strong> (zpracovávají údaje pro nás na
            základě smlouvy o zpracování):
          </p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>poskytovatel hostingu a IT infrastruktury</li>
            <li>poskytovatel e-mailových služeb (rozesílka přihlášek, potvrzení a novinek)</li>
            <li>
              <strong className="text-foreground">Cloudflare</strong> – ochrana formulářů před boty
              a spamem (Cloudflare Turnstile)
            </li>
            <li>nástroj pro monitoring chyb a bezpečnosti aplikace (je-li nasazen)</li>
          </ul>
          <p className="text-muted-foreground">
            <strong className="text-foreground">Samostatní správci</strong> (zpracovávají údaje na
            vlastní odpovědnost): účetní nebo daňový poradce.
          </p>
          <p className="text-muted-foreground">
            <strong className="text-foreground">Orgány veřejné moci</strong>, pokud nám jejich
            poskytnutí ukládá zákon.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">5. Předávání mimo EU</h2>
          <p className="text-muted-foreground">
            Při ochraně formulářů před spamem (Cloudflare Turnstile) a případně při monitoringu chyb
            může docházet ke zpracování údajů i mimo Evropský hospodářský prostor. Takové předání je
            kryto vhodnými zárukami podle čl. 46 GDPR — zpravidla standardními smluvními doložkami
            schválenými Evropskou komisí. Kopii nebo bližší informace k zárukám vám na vyžádání
            poskytneme na e-mailu uvedeném výše.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">6. Cookies a měřicí nástroje</h2>
          <p className="text-muted-foreground">
            Veřejné stránky nepoužívají analytické ani marketingové cookies. Technicky nezbytné
            cookies (relace přihlášení) se uplatní až po přihlášení do interní aplikace. Pokud v
            budoucnu zavedeme měřicí nebo marketingové nástroje, budeme je používat jen s vaším
            souhlasem.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">7. Automatizované rozhodování</h2>
          <p className="text-muted-foreground">
            Neprovádíme automatizované rozhodování ani profilování, které by pro vás mělo právní
            účinky. Výstup z online diagnózy slouží pouze jako orientační podklad pro další
            konzultaci.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">8. Zabezpečení</h2>
          <p className="text-muted-foreground">
            Přijali jsme přiměřená technická a organizační opatření k ochraně údajů – zejména
            šifrovaný přenos (HTTPS), řízení přístupových oprávnění podle rolí a pravidelné
            zálohování.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">9. Vaše práva</h2>
          <p className="text-muted-foreground">Ve vztahu ke svým údajům máte právo na:</p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>přístup ke svým údajům a jejich kopii</li>
            <li>opravu nepřesných nebo doplnění neúplných údajů</li>
            <li>výmaz údajů, pokud to zákon umožní</li>
            <li>omezení zpracování a přenositelnost údajů</li>
            <li>vznesení námitky proti zpracování z důvodu oprávněného zájmu</li>
            <li>
              odvolání souhlasu kdykoli, aniž je dotčena zákonnost zpracování před jeho odvoláním;
              odběr novinek lze odhlásit i odkazem v každém e-mailu
            </li>
          </ul>
          <p className="text-muted-foreground">
            Práva uplatníte e-mailem na{" "}
            <a className="underline" href="mailto:organizace@hcamoravia.cz">
              organizace@hcamoravia.cz
            </a>
            . Pro ověření totožnosti vás můžeme požádat o doplňující údaj. Žádost vyřídíme
            nejpozději do jednoho měsíce.
          </p>
          <p className="text-muted-foreground">
            Pokud máte za to, že zpracováváme údaje v rozporu se zákonem, máte právo podat stížnost
            u dozorového úřadu:{" "}
            <strong className="text-foreground">Úřad pro ochranu osobních údajů</strong>, Pplk.
            Sochora 727/27, Holešovice, 170 00 Praha 7,{" "}
            <a
              className="underline"
              href="https://www.uoou.gov.cz"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.uoou.gov.cz
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold">10. Změny těchto zásad</h2>
          <p className="text-muted-foreground">
            Zásady můžeme aktualizovat, například při zavedení nové služby nebo nástroje. Vždy platí
            aktuální verze zveřejněná na této stránce s uvedeným datem účinnosti.
          </p>
        </section>
      </div>
    </div>
  );
}
